<template>
  <div class="skeleton-screen" :class="[`skeleton-${variant}`, { 'skeleton-animated': animated }]">
    <!-- 自定义布局 -->
    <template v-if="variant === 'custom'">
      <slot>
        <van-skeleton v-for="(item, index) in  categoryCount" :key="index" >
          <template #template>
            <div class="skeleton-custom-item">
              
            </div>
          </template>
        </van-skeleton>
      </slot>
    </template>

    <!-- 卡片布局 -->
    <template v-else-if="variant === 'card'">
      <van-skeleton v-for="(item, index) in  categoryCount" :key="index" >
          <template #template>
            <div class="skeleton-card">
              <
            </div>
          </template>
        </van-skeleton>
    </template>

    <!-- 列表布局 -->
    <template v-else-if="variant === 'list'">
      <div class="skeleton-list">
        <van-skeleton v-for="i in listCount" :key="i" :loading="loading" :avatar="true" :avatar-size="avatarSize"
          :avatar-shape="avatarShape" :title="true" :row="1" :round="round" class="skeleton-list-item" />
      </div>
    </template>

    <!-- 网格布局 -->
    <template v-else-if="variant === 'grid'">
      <div class="skeleton-grid" :style="{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }">
        <van-skeleton v-for="i in gridCount" :key="i" :loading="loading" :avatar="false" :title="false" :row="2"
          :round="round" class="skeleton-grid-item" />
      </div>
    </template>

    <!-- 文章布局 -->
    <template v-else-if="variant === 'article'">
      <div class="skeleton-article">
        <van-skeleton :loading="loading" :avatar="false" :title="true" :row="3" :round="round"
          class="skeleton-article-title" />
        <van-skeleton :loading="loading" :avatar="false" :title="false" :row="4" :round="round"
          class="skeleton-article-content" />
        <van-skeleton :loading="loading" :avatar="true" :avatar-size="avatarSize" :avatar-shape="avatarShape"
          :title="true" :row="1" :round="round" class="skeleton-article-author" />
      </div>
    </template>

    <!-- 用户信息布局 -->
    <template v-else-if="variant === 'profile'">
      <div class="skeleton-profile">
        <van-skeleton :loading="loading" :avatar="true" :avatar-size="80" :avatar-shape="avatarShape" :title="true"
          :row="2" :round="round" class="skeleton-profile-header" />
        <van-skeleton :loading="loading" :avatar="false" :title="false" :row="3" :round="round"
          class="skeleton-profile-content" />
      </div>
    </template>

    <!-- 聊天布局 -->
    <template v-else-if="variant === 'chat'">
      <div class="skeleton-chat">
        <van-skeleton v-for="i in chatCount" :key="i" :loading="loading" :avatar="true" :avatar-size="avatarSize"
          :avatar-shape="avatarShape" :title="false" :row="1" :round="round"
          :class="['skeleton-chat-item', i % 2 === 0 ? 'skeleton-chat-right' : 'skeleton-chat-left']" />
      </div>
    </template>

    <!-- 默认布局 -->
    <template v-else>
      <van-skeleton :loading="loading" :avatar="avatar" :avatar-size="avatarSize" :avatar-shape="avatarShape"
        :title="title" :row="row" :round="round" :row-width="rowWidth" :row-height="rowHeight" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { theme } from '@/styles/theme'

// 骨架屏变体类型
export type SkeletonVariant =
  | 'default'
  | 'card'
  | 'list'
  | 'grid'
  | 'article'
  | 'profile'
  | 'chat'
  | 'custom'

// 自定义骨架项类型
export interface SkeletonItem {
  title?: boolean
  row?: number
  avatar?: boolean
  avatarSize?: string | number
  avatarShape?: 'round' | 'square'
  rowWidth?: string | number | (string | number)[]
  rowHeight?: string | number
  round?: boolean
  className?: string
}

// 组件属性
interface Props {
  // 基础属性
  loading?: boolean
  animated?: boolean
  variant?: SkeletonVariant

  // 默认布局属性
  avatar?: boolean
  avatarSize?: string | number
  avatarShape?: 'round' | 'square'
  title?: boolean
  row?: number
  round?: boolean
  rowWidth?: string | number | (string | number)[]
  rowHeight?: string | number

  // 列表布局属性
  listCount?: number

  // 网格布局属性
  gridColumns?: number
  gridCount?: number

  // 聊天布局属性
  chatCount?: number

  // 自定义布局属性
  customItems?: SkeletonItem[]
  categoryCount?: number
}

const props = withDefaults(defineProps<Props>(), {
  loading: true,
  animated: true,
  variant: 'default',
  avatar: false,
  avatarSize: 32,
  avatarShape: 'round',
  title: true,
  row: 3,
  round: true,
  listCount: 3,
  gridColumns: 2,
  gridCount: 6,
  chatCount: 4,
  customItems: () => [],
  categoryCount: 5
})

// 计算属性
const skeletonTheme = computed(() => ({
  backgroundColor: theme.colors.backgroundSecondary,
  highlightColor: theme.colors.background
}))
</script>

<style scoped lang="scss">
.skeleton-screen {
  width: 100%;

  &.skeleton-animated {
    :deep(.van-skeleton) {
      animation: skeleton-loading 1.5s ease-in-out infinite;
    }
  }

  .skeleton-custom-item {
    padding: 12px;
    background: v-bind('theme.colors.backgroundSecondary');
    width: 335px;
    height: 100px;
    // background: #1B1D32;
    border-radius: 10px;
  }
}

// 卡片布局
.skeleton-card {
  padding: 16px;
  background: v-bind('theme.colors.backgroundSecondary');
  border-radius: v-bind('theme.borderRadius.medium');
  margin-bottom: 16px;
}

// 列表布局
.skeleton-list {
  .skeleton-list-item {
    margin-bottom: 16px;
    padding: 12px;
    background: v-bind('theme.colors.backgroundSecondary');
    border-radius: v-bind('theme.borderRadius.medium');
  }
}

// 网格布局
.skeleton-grid {
  display: grid;
  gap: 16px;

  .skeleton-grid-item {
    padding: 16px;
    background: v-bind('theme.colors.backgroundSecondary');
    border-radius: v-bind('theme.borderRadius.medium');
    min-height: 120px;
  }
}

// 文章布局
.skeleton-article {
  .skeleton-article-title {
    margin-bottom: 16px;
    padding: 16px;
    background: v-bind('theme.colors.backgroundSecondary');
    border-radius: v-bind('theme.borderRadius.medium');
  }

  .skeleton-article-content {
    margin-bottom: 16px;
    padding: 16px;
    background: v-bind('theme.colors.backgroundSecondary');
    border-radius: v-bind('theme.borderRadius.medium');
  }

  .skeleton-article-author {
    padding: 12px;
    background: v-bind('theme.colors.backgroundSecondary');
    border-radius: v-bind('theme.borderRadius.medium');
  }
}

// 用户信息布局
.skeleton-profile {
  .skeleton-profile-header {
    margin-bottom: 16px;
    padding: 20px;
    background: v-bind('theme.colors.backgroundSecondary');
    border-radius: v-bind('theme.borderRadius.medium');
  }

  .skeleton-profile-content {
    padding: 16px;
    background: v-bind('theme.colors.backgroundSecondary');
    border-radius: v-bind('theme.borderRadius.medium');
  }
}

// 聊天布局
.skeleton-chat {
  .skeleton-chat-item {
    margin-bottom: 12px;
    padding: 12px;
    background: v-bind('theme.colors.backgroundSecondary');
    border-radius: v-bind('theme.borderRadius.medium');
    max-width: 80%;

    &.skeleton-chat-left {
      margin-right: auto;
    }

    &.skeleton-chat-right {
      margin-left: auto;
    }
  }
}

// 骨架屏动画
@keyframes skeleton-loading {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.6;
  }

  100% {
    opacity: 1;
  }
}

// 自定义 Vant 骨架屏样式
:deep(.van-skeleton) {
  background-color: v-bind('theme.colors.backgroundSecondary') !important;

  .van-skeleton__avatar {
    background-color: v-bind('theme.colors.background') !important;
  }

  .van-skeleton__title {
    background-color: v-bind('theme.colors.background') !important;
  }

  .van-skeleton__row {
    background-color: v-bind('theme.colors.background') !important;
  }

  .van-skeleton__content {
    background-color: v-bind('theme.colors.backgroundSecondary') !important;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .skeleton-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 12px;
  }

  .skeleton-card,
  .skeleton-list-item,
  .skeleton-article-title,
  .skeleton-article-content,
  .skeleton-profile-header,
  .skeleton-profile-content {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .skeleton-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>
